export function isVideoDeviceOffline(patient) {
  return !patient.room || (patient.video_device && patient.video_device.status !== 'online')
}

export function isFixedDeviceOffline(patient) {
  return patient.video_device &&
         patient.video_device.kind === 'fixed' &&
         isVideoDeviceOffline(patient)
}

export function offlineDeviceTooltip(patient) {
  return {
    content: 'Video device is offline',
    disabled: !isVideoDeviceOffline(patient),
    placement: 'bottom'
  }
}

// Single source of truth for every AI scorecard trait. To add a new trait,
// append an entry here — the icon bar, list column, tooltips, AND the
// table filter's enum options all consume it.
//
// Field shape:
//   key      — key emitted by the AI service
//   type     — 'boolean' | 'enum'
//   unknown  — { icon, tooltip } used when the key is null/missing
//   For boolean: states.true / states.false — state spec
//   For enum:    values[v]                  — state spec (one per possible value)
//
// State spec:
//   icon     — mdi class for the chip icon
//   tooltip  — chip tooltip text
//   label    — short text for the comma-separated list (null/omit to skip)
//   active   — true means "on" (green chip)
//   filter   — { key, label } to expose this state as a filter option
//              (omit to exclude from the filter dropdown)
export const AI_SCORECARD_FIELDS = [
  {
    key: 'patient_location',
    type: 'enum',
    unknown: { icon: 'mdi-map-marker-question-outline', tooltip: 'Patient location unknown' },
    values: {
      bed: {
        icon: 'mdi-bed', tooltip: 'Patient in bed', label: 'In Bed', active: true,
        filter: { key: 'location_bed', label: 'Location: Bed' }
      },
      chair: {
        icon: 'mdi-chair-rolling', tooltip: 'Patient in chair', label: 'In Chair', active: true,
        filter: { key: 'location_chair', label: 'Location: Chair' }
      },
      in_room: {
        icon: 'mdi-account', tooltip: 'Patient in room', label: 'In Room', active: true,
        filter: { key: 'location_in_room', label: 'Location: In Room' }
      },
      not_located: {
        icon: 'mdi-map-marker-question-outline', tooltip: 'Patient not located',
        label: 'Not Located', active: false,
        filter: { key: 'location_not_located', label: 'Location: Not Located' }
      }
    }
  },
  {
    key: 'is_staff_present',
    type: 'boolean',
    unknown: { icon: 'mdi-stethoscope', tooltip: 'Staff presence: unknown' },
    states: {
      true: {
        icon: 'mdi-stethoscope', tooltip: 'Staff present in room', label: 'Staff Present', active: true,
        filter: { key: 'staff_present', label: 'Staff Present' }
      },
      false: {
        icon: 'mdi-stethoscope', tooltip: 'No staff in room', label: 'No Staff', active: false
      }
    }
  },
  {
    key: 'is_tray_present',
    type: 'boolean',
    unknown: { icon: 'mdi-silverware-fork-knife', tooltip: 'Food tray: unknown' },
    states: {
      true: {
        icon: 'mdi-silverware-fork-knife', tooltip: 'Food tray present in room', label: 'Food Tray', active: true,
        filter: { key: 'tray_present', label: 'Food Tray Present' }
      },
      false: {
        icon: 'mdi-silverware-fork-knife', tooltip: 'No food tray in room', label: 'No Tray', active: false
      }
    }
  },
  {
    key: 'is_patient_awake',
    type: 'boolean',
    unknown: { icon: 'mdi-eye-outline', tooltip: 'Patient awake status unknown' },
    states: {
      true: {
        icon: 'mdi-eye-outline', tooltip: 'Patient awake', label: 'Patient Awake', active: false,
        filter: { key: 'patient_awake', label: 'Patient Awake' }
      },
      false: {
        icon: 'mdi-sleep', tooltip: 'Patient asleep', label: 'Patient Asleep', active: true
      }
    }
  }
]

export function aiScorecardFilterEnumValues() {
  const out = []
  for (const field of AI_SCORECARD_FIELDS) {
    const specs = field.type === 'boolean' ? Object.values(field.states) : Object.values(field.values)
    for (const spec of specs) {
      if (spec.filter) out.push({ key: spec.filter.key, value: spec.filter.label })
    }
  }
  return out
}

export function flattenAiScorecard(scorecard) {
  if (!Array.isArray(scorecard) || scorecard.length === 0) return {}
  return scorecard.reduce((acc, entry) => entry ? { ...acc, ...entry } : acc, {})
}

export function resolveAiScorecardField(field, flat) {
  const raw = flat[field.key]
  if (raw === null || raw === undefined) {
    return { state: 'unknown', icon: field.unknown.icon, tooltip: field.unknown.tooltip, label: null }
  }
  const spec = field.type === 'boolean' ? field.states[raw] : field.values[raw]
  if (!spec) {
    return { state: 'unknown', icon: field.unknown.icon, tooltip: field.unknown.tooltip, label: null }
  }
  return {
    state: spec.active ? 'active' : 'inactive',
    icon: spec.icon,
    tooltip: spec.tooltip,
    label: spec.label ?? null
  }
}

export function resolveAiScorecard(scorecard) {
  const flat = flattenAiScorecard(scorecard)
  return AI_SCORECARD_FIELDS.map(field => ({ field, ...resolveAiScorecardField(field, flat) }))
}